import { z } from "zod";
import {
  User,
  DefaultService,
  AddressType,
  EmailType,
  PhoneNumberType,
} from "../../api/SolarCloudApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import getUserQueryOptions from "./useUser";
import { MutationConfig } from "@/lib/react-query";

export const addressSchema = z.object({
  country: z.string().min(2, {
    message: "Please enter your country",
  }),
  line1: z.string().min(1, { message: "Please enter your street address" }),
  line2: z.string().optional(),
  line3: z.string().optional(),
  line4: z.string().optional(),
  city: z.string().min(1, { message: "" }),
  state: z.string().optional(),
  postcode: z.string().optional(),
  type: z.nativeEnum(AddressType, {
    errorMap: () => ({ message: "Address type is required" }),
  }),
  uuid: z.string().optional(),
});

export const phoneNumberSchema = z
  .string()
  .refine(
    (val) => val === "" || /^\+?[\d\s\-()]+$/.test(val),
    { message: "Please enter a valid phone number" }
  );
export const phoneNumbersSchema = z.object({
  uuid: z.string().optional(),
  type: z.nativeEnum(PhoneNumberType).optional(),
  number: phoneNumberSchema,
});

export const emailSchema = z.object({
  uuid: z.string().optional(),
  type: z.nativeEnum(EmailType).optional(),
  address: z
    .string()
    .min(1, { message: "Please enter your email" })
    .email({ message: "Please enter a valid email" }),
});

export const updateUserProfileInputSchema = z.object({
  givenName: z
    .string()
    .min(1, { message: "Please enter your first name" })
    .optional(),
  familyName: z
    .string()
    .min(1, { message: "Please enter your last name" })
    .optional(),
  emails: z.array(emailSchema).optional(),
  phoneNumbers: z.array(phoneNumbersSchema).optional(),
  addresses: z.array(addressSchema).optional(),
});

export type UpdateUserProfileInput = z.infer<
  typeof updateUserProfileInputSchema
>;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const isOptimisticLockError = (error: unknown): boolean => {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : JSON.stringify(error);

  return (
    message.includes("OptimisticLockException") ||
    message.includes("objectChangedSinceLastReadWhenUpdating") ||
    message.includes("changed or been deleted since it was last read")
  );
};

let profileUpdateQueue: Promise<unknown> = Promise.resolve();

async function updateUserProfileWithRetry(data: UpdateUserProfileInput) {
  try {
    return await DefaultService.updateProfile(data as User);
  } catch (error) {
    if (!isOptimisticLockError(error)) {
      throw error;
    }

    // Retry once after a short delay to avoid overlapping writes.
    await sleep(250);
    return DefaultService.updateProfile(data as User);
  }
}

export function updateUserProfile(data: UpdateUserProfileInput) {
  const run = profileUpdateQueue.then(
    () => updateUserProfileWithRetry(data),
    () => updateUserProfileWithRetry(data)
  );

  profileUpdateQueue = run.then(
    () => undefined,
    () => undefined
  );

  return run;
}

type UseUpdateUserProfileOptions = {
  mutationConfig?: MutationConfig<typeof updateUserProfile>;
};

export function useUpdateUserProfile({
  mutationConfig,
}: UseUpdateUserProfileOptions = {}) {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};
  const userQueryKey = getUserQueryOptions().queryKey;

  return useMutation({
    onSuccess: async (data, ...args) => {
      await queryClient.invalidateQueries({ queryKey: userQueryKey });
      await queryClient.refetchQueries({ queryKey: userQueryKey });
      await queryClient.invalidateQueries({ queryKey: ["trackerEvents"] });
      await queryClient.refetchQueries({ queryKey: ["trackerEvents"] });
      onSuccess?.(data, ...args);
    },
    onError: (error) => {
      console.error("Error updating user profile:", error);
    },
    ...restConfig,
    mutationFn: updateUserProfile,
  });
}
